import { FC } from "react";

export type TablePaginationProps = {
  /** Current page, 1-based. */
  page: number;
  pageSize: number;
  /** Total number of rows across all pages. */
  total: number;
  onPageChange: (page: number) => void;
  /** Omit to hide the rows-per-page selector. */
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  /** Noun used in the summary, e.g. "students". Defaults to "records". */
  itemLabel?: string;
  className?: string;
};

/** Page numbers to show around the current page, with "…" gaps. */
function pageWindow(page: number, totalPages: number): (number | "gap")[] {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
  const pages: (number | "gap")[] = [1];
  const from = Math.max(2, page - 1);
  const to = Math.min(totalPages - 1, page + 1);
  if (from > 2) pages.push("gap");
  for (let p = from; p <= to; p++) pages.push(p);
  if (to < totalPages - 1) pages.push("gap");
  pages.push(totalPages);
  return pages;
}

/**
 * Shared table footer: "Showing 1–25 of 230", rows-per-page, and page buttons.
 * Works with usePagination (spread `pager.paginationProps`) or with
 * server-side paging (convert a 0-based page to 1-based before passing it in).
 * On phones the numbered buttons collapse to Prev · 3 / 12 · Next.
 */
const TablePagination: FC<TablePaginationProps> = ({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  itemLabel = "records",
  className = "",
}) => {
  if (total <= 0) return null;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const go = (p: number) => {
    if (p >= 1 && p <= totalPages && p !== page) onPageChange(p);
  };

  return (
    <>
      <TablePaginationStyles />
      <div className={`c9-pager ${className}`}>
        <div className="c9-pager-info">
          Showing <strong>{from.toLocaleString()}</strong>–<strong>{to.toLocaleString()}</strong> of{" "}
          <strong>{total.toLocaleString()}</strong> {itemLabel}
        </div>
        <div className="c9-pager-controls">
          {onPageSizeChange && (
            <select
              className="form-select form-select-sm c9-pager-size"
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              aria-label="Rows per page"
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </select>
          )}
          {totalPages > 1 && (
            <div className="c9-pager-buttons">
              <button
                type="button"
                className="c9-pager-btn"
                disabled={page <= 1}
                onClick={() => go(page - 1)}
                aria-label="Previous page"
              >
                <i className="bi bi-chevron-left" />
              </button>
              {pageWindow(page, totalPages).map((p, i) =>
                p === "gap" ? (
                  <span key={`gap-${i}`} className="c9-pager-gap">
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    className={`c9-pager-btn c9-pager-num${p === page ? " active" : ""}`}
                    onClick={() => go(p)}
                    aria-current={p === page ? "page" : undefined}
                  >
                    {p}
                  </button>
                )
              )}
              <span className="c9-pager-compact">
                {page} / {totalPages}
              </span>
              <button
                type="button"
                className="c9-pager-btn"
                disabled={page >= totalPages}
                onClick={() => go(page + 1)}
                aria-label="Next page"
              >
                <i className="bi bi-chevron-right" />
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const TablePaginationStyles: FC = () => (
  <style>{`
    .c9-pager {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 10px 16px;
      padding: 12px 4px 4px;
      font-size: 0.82rem;
      color: #6b7280;
    }
    .c9-pager-info strong {
      color: #374151;
      font-weight: 600;
      font-variant-numeric: tabular-nums;
    }
    .c9-pager-controls {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;
    }
    .c9-pager-size {
      width: auto;
      min-width: 0;
      padding-top: 4px;
      padding-bottom: 4px;
      font-size: 0.8rem;
    }
    .c9-pager-buttons {
      display: flex;
      align-items: center;
      gap: 4px;
    }
    .c9-pager-btn {
      min-width: 32px;
      height: 32px;
      padding: 0 8px;
      border-radius: 8px;
      border: 1px solid #e5e7eb;
      background: #ffffff;
      color: #374151;
      font-size: 0.8rem;
      font-weight: 600;
      font-variant-numeric: tabular-nums;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: background 150ms ease, border-color 150ms ease;
    }
    .c9-pager-btn:hover:not(:disabled):not(.active) {
      background: #f3f4f6;
      border-color: #d1d5db;
    }
    .c9-pager-btn:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
    .c9-pager-btn.active {
      background: #0f172a;
      border-color: #0f172a;
      color: #ffffff;
    }
    .c9-pager-gap {
      padding: 0 2px;
      color: #9ca3af;
    }
    .c9-pager-compact {
      display: none;
      padding: 0 8px;
      font-weight: 600;
      color: #374151;
      font-variant-numeric: tabular-nums;
    }
    @media (max-width: 575.98px) {
      .c9-pager {
        flex-direction: column;
        align-items: stretch;
      }
      .c9-pager-info { text-align: center; }
      .c9-pager-controls { justify-content: space-between; }
      .c9-pager-num,
      .c9-pager-gap { display: none; }
      .c9-pager-compact { display: inline; }
      .c9-pager-btn { min-width: 40px; height: 40px; }
    }
  `}</style>
);

export default TablePagination;
